import {
  Fragment,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { connect } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { some, isEmpty, isString } from 'lodash';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import classNames from 'classnames';

import * as actions from '../../store/actions/index';
import { formatCSVMemberData } from '../../helpers';

import Button from '../UI/Buttons/Index';
import Filter from '../Filters/Index';
import FilterExcel from '../Filters/Excel';
import ExportFile from '../Exports/Index';
import Modal from '../UI/Modal/Index';
import Navigation from '../UI/Navigation/Index';
import Table from '../UI/Tables/Index';

const Index = (props) => {
  const [barangay, setBarangay] = useState(
    'e05f8724-a798-4942-9108-bfea7dc5eabd'
  );
  const [barangayOptions, setBarangayOptions] = useState([]);
  const [download, setDownload] = useState(false);
  const [downloadNoIdMembers, setDownloadNoIdMembers] = useState(false);
  const [exportedItems, setExportedItems] = useState([]);
  const [filterParams, setFilterParams] = useState([]);
  const [id, setId] = useState('');
  const [memberItems, setMemberItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [openNoIdModal, setOpenNoIdModal] = useState(false);
  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [pageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [paginationItems, setPaginationItems] = useState({});
  const [role, setRole] = useState('');
  const [status, setStatus] = useState(1);

  const {
    barangays,
    exportedMembers,
    isFilter,
    loading,
    memberLoading,
    members,
    noIdMembers,
    onExportMembers,
    onFetchAllMembers,
    onFetchAllMembersWithoutId,
    onFetchBarangays,
    onFilterMembers,
    onResetMembers,
    onUpdateRole,
    pagination,
  } = props;

  const navigate = useNavigate();
  const filterRef = useRef(null);

  useEffect(() => {
    onFetchBarangays();
  }, [onFetchBarangays]);

  useEffect(() => {
    if (exportedMembers) {
      setExportedItems(exportedMembers);
    }
  }, [exportedMembers]);

  useEffect(() => {
    onFetchAllMembers({ pageIndex, pageSize });
  }, [onFetchAllMembers, pageIndex, pageSize]);

  useEffect(() => {
    if (members) {
      setMemberItems(members);
      return () => {
        setDownload(false);
      };
    }
  }, [members, download]);

  useEffect(() => {
    if (pagination) {
      setPaginationItems(pagination);
    }
  }, [pagination]);

  useEffect(() => {
    if (noIdMembers) {
      setTimeout(() => {
        setDownloadNoIdMembers(true);
      }, 1000);
    }
    return () => {
      setDownloadNoIdMembers(false);
      setOpenNoIdModal(false);
    };
  }, [noIdMembers]);

  useEffect(() => {
    if (barangays) {
      const brs = barangays.map((b) => {
        return (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        );
      });
      setBarangayOptions(brs);
    }
  }, [barangays]);

  const addNewButtonHandler = () => {
    onResetMembers();
    return navigate('/members/create');
  };

  const openExcelHandler = () => {
    setOpen(true);
    setDownloadNoIdMembers(false);
  };

  const openIdExcelHandler = () => {
    setOpenNoIdModal(true);
  };

  useEffect(() => {
    if (exportedItems.length > 0 && download === false) {
      setDownload(true); // triggers <ExportFile> rendering
    }
  }, [exportedItems]);

  const downloadExcelHandler = useCallback(async () => {
    const data = filterRef.current;
    const hasFilter = some(data, (value) => {
      return isString(value) && !isEmpty(value);
    });

    if (hasFilter) {
      await onExportMembers(data);
      setDownload(true);
      setOpen(false);
    }
  }, []);

  const openRoleModalHandler = (role, id) => {
    setId(id);
    setRole(role);
    setOpenRoleModal(true);
  };

  const updateRoleHandler = useCallback(() => {
    const data = {
      id: id,
      role: role,
    };
    onUpdateRole(data);
    setTimeout(() => {
      setOpenRoleModal(false);
      toast.success('Role is Updated!', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    }, 100);
  }, [role, id]);

  const columns = useMemo(
    () => [
      {
        Header: 'Member Data',
        hideHeader: false,
        columns: [
          {
            Header: 'ID',
            accessor: 'mid',
            Cell: ({ value, row }) => (
              <Link
                to={{
                  pathname: '/members/view',
                  search: `?id=${row.original.id}`,
                }}
                state={{ id: row.original.id }}
              >
                <b>{value}</b>
              </Link>
            ),
          },
          {
            Header: 'LastName',
            accessor: 'lastname',
          },
          {
            Header: 'FirstName',
            accessor: 'firstname',
          },
          {
            Header: 'MiddleName',
            accessor: 'middlename',
          },
          {
            Header: 'Birthdate',
            accessor: 'birthdate',
          },
          {
            Header: 'ID Status',
            accessor: 'idStatus',
          },
          {
            Header: 'Barangay',
            accessor: 'barangay',
          },
          {
            Header: 'Valid Until',
            accessor: 'validity',
          },
          {
            Header: 'Type',
            accessor: 'type',
          },
          {
            Header: 'Role',
            accessor: 'role',
            Cell: ({ value, row }) => (
              <span
                onClick={() => openRoleModalHandler(value, row.original.id)}
                className={classNames(
                  'text-xs font-semibold mr-2 px-2.5 py-0.5 rounded',
                  value === 'PRESIDENT'
                    ? 'bg-blue-100 text-blue-800  dark:bg-blue-200 dark:text-blue-800'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-200 dark:text-gray-800'
                )}
              >
                {value}
              </span>
            ),
          },
          {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ value }) => (
              <span
                className={classNames(
                  'text-xs font-semibold mr-2 px-2.5 py-0.5 rounded',
                  value === 'Active'
                    ? 'bg-green-100 text-green-800  dark:bg-green-200 dark:text-green-800'
                    : 'bg-red-100 text-red-800 dark:bg-red-200 dark:text-red-800'
                )}
              >
                {value}
              </span>
            ),
          },
        ],
      },
    ],
    []
  );

  const fetchUpdatedData = ({ pageIndex, pageSize }) => {
    const baseParams = { pageIndex, pageSize };
    if (isFilter && filterParams) {
      onFilterMembers({
        ...filterParams,
        ...baseParams,
      });
    } else {
      onFetchAllMembers(baseParams);
    }
  };

  return (
    <Fragment>
      <Navigation />
      <ToastContainer />
      {open && (
        <Modal
          title="Filter Data"
          btnLabel="Download"
          cancel={() => setOpen(false)}
          enter={() => downloadExcelHandler()}
          loading={loading}
        >
          <FilterExcel filterRef={filterRef} />
        </Modal>
      )}
      {openRoleModal && (
        <Modal
          title="Edit Role"
          btnLabel="Update"
          cancel={() => setOpenRoleModal(false)}
          enter={() => updateRoleHandler()}
          loading={loading}
        >
          <select
            className="block appearance-none w-full border border-gray-200 text-gray-700 py-3 px-4 pr-8 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
            id="civilStatus"
            name="civilStatus"
            type="text"
            onChange={(e) => setRole(e.target.value)}
          >
            <option
              key="1"
              value="PRESIDENT"
              selected={role === 'PRESIDENT' ? true : false}
            >
              PRESIDENT
            </option>
            <option
              key="2"
              value="MEMBER"
              selected={role === 'MEMBER' ? true : false}
            >
              MEMBER
            </option>
          </select>
        </Modal>
      )}
      {openNoIdModal && (
        <Modal
          title="Download List"
          btnLabel="Download"
          cancel={() => setOpenNoIdModal(false)}
          enter={() => onFetchAllMembersWithoutId({ barangay, status })}
          loading={loading}
        >
          <div className="flex gap-4">
            <div className="flex-1 flex flex-col">
              <label
                htmlFor="barangay"
                className="mb-1 text-gray-700 font-medium"
              >
                Barangay
              </label>
              <select
                className="appearance-none w-full border border-gray-200 text-gray-700 py-2 px-4 pr-8 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                id="barangay"
                name="barangay"
                value={barangay}
                onChange={(e) => setBarangay(e.target.value)}
              >
                {barangayOptions}
              </select>
            </div>

            <div className="flex-1 flex flex-col">
              <label
                htmlFor="status"
                className="mb-1 text-gray-700 font-medium"
              >
                Membership Status
              </label>
              <select
                className="appearance-none w-full border border-gray-200 text-gray-700 py-2 px-4 pr-8 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                id="status"
                name="status"
                value={status}
                onChange={(e) => setStatus(Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>In Active</option>
              </select>
            </div>
          </div>
        </Modal>
      )}
      {download && exportedItems.length > 0 && (
        <ExportFile data={formatCSVMemberData(exportedItems)} name="Members" />
      )}
      {downloadNoIdMembers && noIdMembers && noIdMembers.length > 0 && (
        <ExportFile data={noIdMembers} name="ID For Printing" />
      )}
      <div className="container mt-6 w-full">
        <Button onClick={addNewButtonHandler} className="mb-4">
          Add New
        </Button>
        {barangays && (
          <Filter
            barangays={barangays}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onFilterMembers={(params) => {
              setFilterParams(params);
              onFilterMembers(params);
            }}
          />
        )}
        <Button
          onClick={openExcelHandler}
          className="h-8 px-4 py-1 text-sm font-medium mb-2 mr-2"
          disabled={false}
        >
          Generate Excel File
        </Button>
        <Button
          onClick={openIdExcelHandler}
          className="h-8 px-4 py-1 text-sm font-medium mb-2"
          disabled={loading}
        >
          {loading ? 'Please Wait...' : 'Generate ID For Printing'}
        </Button>
        {/* Display Members on a table */}
        <Table
          columns={columns}
          tableData={memberItems}
          loading={memberLoading}
          pagination={paginationItems}
          fetchUpdatedData={(params) => fetchUpdatedData(params)}
        />
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    barangays: state.barangays.barangays,
    error: state.members.error,
    exportedMembers: state.members.exportedMembers,
    isFilter: state.members.filter,
    loading: state.members.loading,
    memberLoading: state.members.fetchMembersLoading,
    members: state.members.members,
    noIdMembers: state.members.noIdMembers,
    occupations: state.occupations.occupations,
    pagination: state.members.pagination,
    updated: state.members.updated,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onExportMembers: (data) => dispatch(actions.exportMembers(data)),
    onFetchAllMembers: (data) => dispatch(actions.fetchAllMembers(data)),
    onFetchAllMembersWithoutId: (data) =>
      dispatch(actions.fetchAllNoIDMembers(data)),
    onFilterMembers: (data) => dispatch(actions.filterMembers(data)),
    onResetMembers: () => dispatch(actions.resetMembers()),
    onUpdateRole: (data) => dispatch(actions.updateRole(data)),
    onFetchBarangays: () => dispatch(actions.fetchAllBarangays()),
    onFetchOccupations: () => dispatch(actions.fetchAllOccupations()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Index);
